import { io } from 'socket.io-client';
import { captureException, captureMessage } from 'shared/sentry';
import {
  SOCKET_EVENTS,
  SOCKET_NOTIFICATION_CONFIG,
  SOCKET_SENTRY_STEPS,
  SOCKET_SENTRY_TAGS,
  resolveNotificationSocketUrl,
} from './socket';

/** @typedef {{ onNotification?: (payload: object) => void, onReady?: (status: object) => void }} SocketSubscriber */

/**
 * Singleton Socket.IO client for authenticated realtime notifications.
 * Ref-counted subscribers; one physical connection per app session.
 */
class NotificationSocketManager {
  constructor() {
    /** @type {import('socket.io-client').Socket | null} */
    this.socket = null;
    this.currentToken = null;
    /** @type {Set<SocketSubscriber>} */
    this.subscribers = new Set();
    this.coreListenersAttached = false;
    this.lastConnectErrorAt = 0;
  }

  /**
   * @param {SocketSubscriber} subscriber
   * @returns {() => void}
   */
  subscribe(subscriber) {
    if (!subscriber || typeof subscriber !== 'object') {
      return () => {};
    }

    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  /**
   * @param {string} token
   * @returns {import('socket.io-client').Socket | null}
   */
  connect(token) {
    if (!token || typeof token !== 'string') {
      return null;
    }

    const url = resolveNotificationSocketUrl();
    if (!url) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[notification-socket] missing socket URL (set REACT_APP_SOCKET_URL, REACT_APP_MESSAGE_WSS, or REACT_APP_API)'
        );
      }
      return null;
    }

    const tokenChanged = this.currentToken && this.currentToken !== token;
    if (tokenChanged) {
      this.teardownSocket();
    }

    this.currentToken = token;

    if (this.socket) {
      this.socket.auth = { token };
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return this.socket;
    }

    this.socket = io(url, {
      transports: ['polling', 'websocket'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: SOCKET_NOTIFICATION_CONFIG.RECONNECTION_ATTEMPTS,
      reconnectionDelay: SOCKET_NOTIFICATION_CONFIG.RECONNECTION_DELAY_MS,
      reconnectionDelayMax: SOCKET_NOTIFICATION_CONFIG.RECONNECTION_DELAY_MAX_MS,
      timeout: SOCKET_NOTIFICATION_CONFIG.TIMEOUT_MS,
      autoConnect: true,
    });

    this.attachCoreListeners();

    if (process.env.NODE_ENV === 'development') {
      console.log('pushNotification >> socket:connecting', url);
    }

    return this.socket;
  }

  attachCoreListeners() {
    if (!this.socket || this.coreListenersAttached) {
      return;
    }

    this.coreListenersAttached = true;

    this.socket.on('connect', () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('pushNotification >> socket:connected', this.socket?.id);
      }
    });

    this.socket.on(SOCKET_EVENTS.READY, (data) => {
      this.emitReady(data);
    });

    this.socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, (payload) => {
      this.emitNotification(payload);
    });

    this.socket.on('disconnect', (reason) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('pushNotification >> socket:disconnected:', reason);
      }
    });

    this.socket.on('connect_error', (err) => {
      const now = Date.now();
      if (now - this.lastConnectErrorAt < 30_000) {
        return;
      }
      this.lastConnectErrorAt = now;

      captureMessage('[notification-socket] connect_error', {
        level: 'warning',
        tags: SOCKET_SENTRY_TAGS,
        extra: {
          step: SOCKET_SENTRY_STEPS.CONNECT,
          message: err?.message,
        },
      });
    });
  }

  /** @param {object} payload */
  emitNotification(payload) {
    for (const subscriber of this.subscribers) {
      try {
        subscriber.onNotification?.(payload);
      } catch (error) {
        captureException(error, {
          tags: SOCKET_SENTRY_TAGS,
          extra: { step: SOCKET_SENTRY_STEPS.HANDLER, phase: 'notification' },
        });
      }
    }
  }

  /** @param {object} status */
  emitReady(status) {
    for (const subscriber of this.subscribers) {
      try {
        subscriber.onReady?.(status);
      } catch (error) {
        captureException(error, {
          tags: SOCKET_SENTRY_TAGS,
          extra: { step: SOCKET_SENTRY_STEPS.HANDLER, phase: 'ready' },
        });
      }
    }
  }

  reconnectIfNeeded() {
    if (!this.currentToken) {
      return;
    }

    if (!this.socket) {
      this.connect(this.currentToken);
      return;
    }

    this.socket.auth = { token: this.currentToken };

    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  teardownSocket() {
    if (!this.socket) {
      this.coreListenersAttached = false;
      return;
    }

    try {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    } catch (error) {
      captureException(error, {
        tags: SOCKET_SENTRY_TAGS,
        extra: { step: SOCKET_SENTRY_STEPS.TEARDOWN },
      });
    }

    this.socket = null;
    this.coreListenersAttached = false;
  }

  disconnect() {
    this.currentToken = null;
    this.teardownSocket();
  }

  isConnected() {
    return Boolean(this.socket?.connected);
  }
}

const manager = new NotificationSocketManager();

/**
 * Subscribe to realtime notifications. Connects (or reuses) the singleton socket.
 * @param {string} token JWT access token
 * @param {(payload: object) => void} [onNotification]
 * @param {(status: object) => void} [onReady]
 * @returns {() => void} unsubscribe (does not disconnect; use disconnectNotificationSocket on logout)
 */
export function subscribeNotificationSocket(token, onNotification, onReady) {
  const subscriber = { onNotification, onReady };
  const unsubscribe = manager.subscribe(subscriber);
  manager.connect(token);

  return () => {
    unsubscribe();
  };
}

/**
 * Connect and register handlers (singleton). Prefer subscribeNotificationSocket when you need cleanup.
 * @returns {import('socket.io-client').Socket | null}
 */
export function connectNotificationSocket(token, onNotification, onReady) {
  subscribeNotificationSocket(token, onNotification, onReady);
  return manager.socket;
}

export function disconnectNotificationSocket() {
  manager.disconnect();
}

/** Safari / background tab recovery: reconnect when visible if the socket dropped */
export function reconnectNotificationSocketIfNeeded() {
  manager.reconnectIfNeeded();
}

export function getNotificationSocket() {
  return manager.socket;
}

export function isNotificationSocketConnected() {
  return manager.isConnected();
}
